import * as Dialog from '@radix-ui/react-dialog'
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Button } from '../primitives/Button'
import { Icon, type IconName } from '../primitives/Icon'
import { Input } from '../primitives/Input'
import { Kbd } from '../primitives/Kbd'
import { cx } from '../cx'
import s from './CommandPalette.module.css'

export interface Command {
  id: string
  label: string
  /** The heading it sits under: "Operate", "Learn", "Actions". */
  group: string
  icon?: IconName
  /** A short note on the right, e.g. the screen's address. */
  hint?: string
  /** Other words that should find it. */
  keywords?: string
  run: () => void
}

/**
 * Jump anywhere by typing: every screen, every Learn page, and a few actions. Opens with ⌘K or Ctrl K anywhere, or
 * from the search button in the sidebar and the phone's top bar. Arrow keys move, Enter runs, Esc closes.
 * The app builds the list (src/app/commands.ts); this only draws and filters it.
 */
export function CommandPalette(props: { open: boolean; onOpenChange: (open: boolean) => void; commands: Command[] }) {
  const { open, onOpenChange } = props
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const listId = useId()
  const listRef = useRef<HTMLDivElement>(null)

  // ⌘K / Ctrl K toggles it from anywhere.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const results = useMemo(() => filterCommands(props.commands, query), [props.commands, query])
  useEffect(() => setActive(0), [query])
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const run = (c: Command | undefined) => {
    if (!c) return
    onOpenChange(false)
    c.run()
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(results.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      run(results[active])
    }
  }

  let lastGroup = ''
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.scrim} />
        <Dialog.Content className={s.palette} aria-describedby={undefined}>
          <Dialog.Title className={s.hidden}>Search or jump to</Dialog.Title>
          <div className={s.search}>
            <Input
              label="Search screens, Learn and actions"
              hideLabel
              borderless
              icon="search"
              value={query}
              onChange={setQuery}
              onKeyDown={onKey}
              placeholder="Search or jump to…"
              autoFocus
              aria={{
                role: 'combobox',
                controls: listId,
                expanded: true,
                ...(results[active] ? { activedescendant: `${listId}-${active}` } : {}),
              }}
            />
            <Kbd>Esc</Kbd>
            <span className={s.closeTouch}>
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </span>
          </div>
          <div className={s.list} id={listId} role="listbox" ref={listRef} aria-label="Results">
            {results.length === 0 && <p className={s.none}>Nothing matches “{query}”.</p>}
            {results.map((c, i) => {
              const heading = c.group !== lastGroup ? c.group : null
              lastGroup = c.group
              return (
                <div key={c.id} role="presentation">
                  {heading && <div className={s.group}>{heading}</div>}
                  <div
                    id={`${listId}-${i}`}
                    data-index={i}
                    role="option"
                    aria-selected={i === active}
                    className={cx(s.item, i === active && s.active)}
                    onMouseMove={() => setActive(i)}
                    onClick={() => run(c)}
                  >
                    {c.icon && <Icon name={c.icon} />}
                    <span className={s.label}>{c.label}</span>
                    {c.hint && <span className={s.hint}>{c.hint}</span>}
                    {i === active && <Icon name="enter" size="sm" />}
                  </div>
                </div>
              )
            })}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/**
 * Commands matching what was typed, best first, grouped in their original order. Every word typed must appear in
 * the label, group or keywords; a label that starts with it ranks first. Empty query: everything, as given.
 */
export function filterCommands(commands: Command[], query: string): Command[] {
  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return commands
  const scored = commands
    .map((c, i) => {
      const label = c.label.toLowerCase()
      const hay = `${label} ${c.group.toLowerCase()} ${(c.keywords ?? '').toLowerCase()} ${(c.hint ?? '').toLowerCase()}`
      if (!words.every((w) => hay.includes(w))) return null
      const score = label.startsWith(words[0]!) ? 0 : label.includes(words[0]!) ? 1 : 2
      return { c, i, score }
    })
    .filter((x): x is { c: Command; i: number; score: number } => x !== null)
  const groups = [...new Set(scored.map((x) => x.c.group))]
  const best = new Map(groups.map((g) => [g, Math.min(...scored.filter((x) => x.c.group === g).map((x) => x.score))]))
  return scored
    .sort((a, b) => best.get(a.c.group)! - best.get(b.c.group)! || groups.indexOf(a.c.group) - groups.indexOf(b.c.group) || a.score - b.score || a.i - b.i)
    .map((x) => x.c)
}
